/**
 * API が返す節の定義（`GET /basic-design/artifacts`）を画面で使うための小さな読み方。
 *
 * 列・選択肢・上限・必須かどうかは定義が持ち、ここには写さない。ここが持つのは画面だけの
 * 判断（節をどのまとまりで並べるか、行 ID をどう振るか、保存済みの節をどう呼ぶか）だけ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節の定義から、選択肢の表示名・節の状態・行 ID の採番・参照できる ID・ナビのまとまりを引く
 * @graph-connects none
 */

import { applyOrder } from '../../../lib/bd-view-settings.js';
import { splitFlowNodes } from './bd-presentation.js';
import type {
  BdArtifact,
  BdArtifactDef,
  BdColumnDef,
  BdRow,
  BdSavedArtifact,
} from '../../../lib/api/basic-design.js';

/**
 * セルの値。保存済みの行は列がそろっているが、古い版や編集中の行に列が欠けていても空文字で読む。
 * @graph-connects none
 */
export function bdCell(row: Readonly<Record<string, string | undefined>>, key: string): string {
  return row[key] ?? '';
}

/** 列の定義を引く。 @graph-connects none */
export function columnOf(def: BdArtifactDef, key: string): BdColumnDef | undefined {
  return def.columns.find((c) => c.key === key);
}

/**
 * 選択肢の値を表示名にする。選択肢から外れた値・選ぶ列でない値は保存された値のまま出す
 * （空欄にすると、何が保存されているのかが画面から分からなくなる）。
 * @graph-connects none
 */
export function labelOf(column: BdColumnDef | undefined, value: string): string {
  return column?.choices?.find((c) => c.value === value)?.label ?? value;
}

/**
 * 節の状態。`notApplicable` は任意の節を 0 行で保存したもの（「該当なし」と書いたことになる）。
 * 必須の節を 0 行で保存したもの（古いデータ）は、次の 1 手と同じく書いていない扱いにする。
 * @graph-connects none
 */
export type BdSectionStatus = 'saved' | 'notSaved' | 'notApplicable';

/** 節の状態を決める。 @graph-connects none */
export function sectionStatus(
  def: BdArtifactDef,
  saved: Pick<BdSavedArtifact, 'rows'> | undefined,
): BdSectionStatus {
  if (saved === undefined) return 'notSaved';
  if (saved.rows.length > 0) return 'saved';
  return def.requirement === 'optional' ? 'notApplicable' : 'notSaved';
}

/** 行 ID の番号（`S12` の 12）。接頭辞が違う・形が違うときは null。 @graph-connects none */
function idNumber(prefix: string, id: string): number | null {
  const match = /^([A-Za-z]+)([1-9][0-9]*)$/.exec(id.trim());
  return match === null || match[1] !== prefix ? null : Number(match[2]);
}

/**
 * 次の行 ID（接頭辞 + 今ある番号の最大 + 1）。
 *
 * 消した行の番号を詰めて振り直すと、ほかの節からの参照（S2 など）が別の行を指してしまう。
 * 一度振った番号は再利用しないよう、空いた番号は埋めず最大の次にする。
 * @graph-connects idNumber [calls] 行 ID の番号
 */
export function nextRowId(prefix: string, rows: readonly BdRow[]): string {
  const max = rows.reduce((m, row) => Math.max(m, idNumber(prefix, bdCell(row, 'id')) ?? 0), 0);
  return `${prefix}${String(max + 1)}`;
}

/** 空の 1 行。行 ID を持つ節は次の ID を振っておく。 @graph-connects nextRowId [calls] 行 ID の採番 */
export function emptyRow(def: BdArtifactDef, rows: readonly BdRow[]): BdRow {
  const row = Object.fromEntries(def.columns.map((c) => [c.key, '']));
  return def.idPrefix === null ? row : { ...row, id: nextRowId(def.idPrefix, rows) };
}

/**
 * 全部空の行か。行 ID は画面が振るので数えない（ID だけの行は書いていない行）。
 * @graph-connects none
 */
export function isBlankRow(def: BdArtifactDef, row: BdRow): boolean {
  return def.columns.every((c) => c.kind === 'id' || bdCell(row, c.key).trim() === '');
}

/** 行を列の並びに揃える（無い列は空文字）。 @graph-connects none */
export function alignRow(
  def: BdArtifactDef,
  row: Readonly<Record<string, string | undefined>>,
): BdRow {
  return Object.fromEntries(def.columns.map((c) => [c.key, bdCell(row, c.key)]));
}

/**
 * 参照の列で指せる ID。同じ節を指す列は編集中の行から（自分の行は除く）、ほかの節は保存済みの行から引く。
 *
 * 保存済みの行だけを見るのは、API の参照のチェック（`ref_unknown`）と同じ。
 * @graph-connects none
 */
export function availableRefIds(input: {
  column: BdColumnDef;
  def: BdArtifactDef;
  formRows: readonly BdRow[];
  ownId: string;
  saved: Readonly<Partial<Record<BdArtifact, Pick<BdSavedArtifact, 'rows'>>>>;
}): string[] {
  const { column, def, formRows, ownId, saved } = input;
  const ids = (column.refTo ?? []).flatMap((target) => {
    const rows = target === def.key ? formRows : (saved[target]?.rows ?? []);
    return rows.map((row) => bdCell(row, 'id').trim());
  });
  return [...new Set(ids)].filter((id) => id !== '' && id !== ownId.trim());
}

/** 参照の列の値を 1 つずつに分ける（API と同じ区切り）。 @graph-connects splitFlowNodes [calls] 区切り */
export function splitRefs(value: string): string[] {
  return splitFlowNodes(value);
}

/** ナビの「全体」を指すキー。節の名前と重ならない。 @graph-connects none */
export const BD_OVERVIEW = 'overview';

/** ナビの「メモ」を指すキー。節の名前と重ならない。 @graph-connects none */
export const BD_NOTES = 'notes';

/**
 * ナビのまとまりと、その中の節。テンプレートの部（1a・1f は読み手が並べて読むので同じまとまり）。
 *
 * ここに無い節（domain で増えたもの）は「その他」に入れて、画面から消えないようにする。
 * @graph-connects none
 */
const GROUPS: readonly { key: string; artifacts: readonly BdArtifact[] }[] = [
  { key: 'impactRisk', artifacts: ['impact_list', 'risk_list'] },
  { key: 'screens', artifacts: ['screens', 'screen_transitions'] },
  {
    key: 'howData',
    artifacts: [
      'approaches',
      'domains',
      'entities',
      'relations',
      'migrations',
      'api_contracts',
      'events',
      'data_operations',
    ],
  },
  {
    key: 'quality',
    artifacts: [
      'validations',
      'nfr_performance',
      'nfr_availability',
      'nfr_security',
      'nfr_observability',
      'error_paths',
      'failure_modes',
    ],
  },
  { key: 'decisions', artifacts: ['decision_logic', 'decisions'] },
  { key: 'compatibility', artifacts: ['breaking_changes', 'versioning'] },
  { key: 'tasks', artifacts: ['tasks'] },
];

/** ナビのまとまり 1 つ。 @graph-connects none */
export interface BdNavGroup {
  key: string;
  defs: BdArtifactDef[];
}

/**
 * 定義をナビのまとまりに分ける。まとまりの中は定義の順（テンプレートの順）か、見ている人が
 * 並べ替えた順（`order`）。空のまとまりは出さない。
 * @graph-connects applyOrder [calls] 個人の並びの適用
 */
export function navGroups(
  defs: readonly BdArtifactDef[],
  order: readonly string[] = [],
): BdNavGroup[] {
  const known = new Set(GROUPS.flatMap((g) => g.artifacts));
  const groups = GROUPS.map((g) => ({
    key: g.key,
    defs: applyOrder(
      defs.filter((d) => g.artifacts.includes(d.key)),
      order,
      (d) => d.key,
    ),
  }));
  const other = { key: 'other', defs: defs.filter((d) => !known.has(d.key)) };
  return [...groups, other].filter((g) => g.defs.length > 0);
}
